import { Button, Dropdown, Modal, Select, Spin, Tag, Tooltip } from "antd";
import { generateClient } from "aws-amplify/api";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import type { CSSProperties, ReactNode } from "react";
import { Project } from "../../API";
import { customGetProject } from "../../graphql/customQueries";
import { AsyncOpState } from "../../stores/async-op-state";
import { getENV } from "../../stores/consts";
import { useMst } from "../../stores/main";
import { HoverTooltip } from "../../BaseComps/InfoTooltip";
import { BlingsBtn } from "../antd-extensions/blings-btn.component";
import PlayerManager from "../../helpers/playerManager";
import { IFlowScene } from "@blings/blings-player";
import classNames from "classnames";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import { flowsStore } from "../../stores/FlowStore";
import { getAccessTokenForLambdaAuth } from "../../utils/authHelpers";
import { savedDate2Relative } from "./PublishStatus";
import { TimeIcon } from "../../icons-and-animations/Icons";
import { DemoWindow } from "../DemoWindow/DemoWindow";
import { getDemoPlayerUrl } from "../../helpers/demoPlayerUrl";
import { getFlowMapEmbedUrl } from "./flowMapUrl";
import { useSaveProjectShortcut } from "../../hooks/useSaveProjectShortcut";
import {
  getUnsavedStudioText,
  hasConnectorDraftChanges,
  hasDataDraftChanges,
  hasPublishableChanges,
  hasUnsavedDraftChanges,
  saveStudioDraftProject,
} from "./publishSaveState";
import "./PublishButtons.scss";

type FlowMapSize = {
  width: number;
  height: number;
};

const FLOW_MAP_MIN_WIDTH = 760;
const FLOW_MAP_MIN_HEIGHT = 520;
const FLOW_MAP_VIEWPORT_PADDING = 48;
const FLOW_MAP_HEADER_HEIGHT = 57;
const SAVE_MODAL_Z_INDEX = 1200;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getDefaultFlowMapSize(): FlowMapSize {
  if (typeof window === "undefined") {
    return { width: 1200, height: 760 };
  }

  const maxWidth = Math.max(360, window.innerWidth - FLOW_MAP_VIEWPORT_PADDING);
  const maxHeight = Math.max(
    420,
    window.innerHeight - FLOW_MAP_VIEWPORT_PADDING
  );

  return {
    width: maxWidth,
    height: maxHeight,
  };
}

const FlowMapIcon = () => (
  <svg
    className="flow-map-icon"
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 15 15"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.7553 4.39024C11.3615 4.39024 11.8529 3.89885 11.8529 3.29268C11.8529 2.68652 11.3615 2.19512 10.7553 2.19512C10.1491 2.19512 9.65773 2.68652 9.65773 3.29268C9.65773 3.89885 10.1491 4.39024 10.7553 4.39024ZM10.7553 5.70731C9.65001 5.70731 8.71817 4.96468 8.43154 3.95117L4.01786 3.95125C3.61692 3.95125 3.29189 4.27629 3.29189 4.67724C3.29189 4.99784 3.50218 5.28048 3.80926 5.37261L10.6164 7.41476C11.4806 7.674 12.0724 8.46938 12.0724 9.37158C12.0724 10.4999 11.1577 11.4146 10.0294 11.4146L5.61564 11.4146C5.32901 12.4281 4.39717 13.1707 3.29189 13.1707C1.95832 13.1707 0.877257 12.0896 0.877257 10.7561C0.877257 9.42252 1.95832 8.34146 3.29189 8.34146C4.39713 8.34146 5.32894 9.08404 5.61561 10.0975L10.0294 10.0975C10.4303 10.0975 10.7553 9.77249 10.7553 9.37158C10.7553 9.05101 10.545 8.7684 10.238 8.67628L3.43081 6.63413C2.56663 6.37488 1.97481 5.57947 1.97481 4.67724C1.97481 3.5489 2.8895 2.6342 4.01784 2.63418L8.43157 2.6341C8.71823 1.62063 9.65004 0.878052 10.7553 0.878052C12.0889 0.878052 13.1699 1.95912 13.1699 3.29268C13.1699 4.62625 12.0889 5.70731 10.7553 5.70731ZM4.38945 10.7561C4.38945 11.3623 3.89805 11.8536 3.29189 11.8536C2.68572 11.8536 2.19433 11.3623 2.19433 10.7561C2.19433 10.1499 2.68572 9.65853 3.29189 9.65853C3.89805 9.65853 4.38945 10.1499 4.38945 10.7561Z"
      fill="currentColor"
    />
  </svg>
);

const DataIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.25 3.5C2.83579 3.5 2.5 3.83579 2.5 4.25V11.75C2.5 12.1642 2.83579 12.5 3.25 12.5H4.26087C4.67508 12.5 5.01087 12.8358 5.01087 13.25C5.01087 13.6642 4.67508 14 4.26087 14H3.25C2.00736 14 1 12.9926 1 11.75V4.25C1 3.00736 2.00736 2 3.25 2H4.26087C4.67508 2 5.01087 2.33579 5.01087 2.75C5.01087 3.16421 4.67508 3.5 4.26087 3.5H3.25ZM10.9891 2.75C10.9891 2.33579 11.3249 2 11.7391 2H12.75C13.9926 2 15 3.00736 15 4.25V11.75C15 12.9926 13.9926 14 12.75 14H11.7391C11.3249 14 10.9891 13.6642 10.9891 13.25C10.9891 12.8358 11.3249 12.5 11.7391 12.5H12.75C13.1642 12.5 13.5 12.1642 13.5 11.75V4.25C13.5 3.83579 13.1642 3.5 12.75 3.5H11.7391C11.3249 3.5 10.9891 3.16421 10.9891 2.75ZM6.79108 5.33778C6.98006 4.83385 7.4618 4.5 8 4.5C8.5382 4.5 9.01995 4.83385 9.20892 5.33778L10.9522 9.98666C11.0977 10.3745 10.9012 10.8068 10.5133 10.9522C10.1255 11.0977 9.69319 10.9012 9.54775 10.5133L9.16775 9.5L6.83225 9.5L6.45225 10.5133C6.30681 10.9012 5.8745 11.0977 5.48666 10.9522C5.09882 10.8068 4.90231 10.3745 5.04775 9.98666L6.79108 5.33778ZM7.39475 8L8.60525 8L8 6.386L7.39475 8Z"
      fill="currentColor"
    />
  </svg>
);

const client = generateClient();

function serializePreviewScene(scene: IFlowScene): string {
  return typeof scene === "string" ? scene : JSON.stringify(scene);
}

function parsePreviewScene(scene: string): IFlowScene {
  try {
    return JSON.parse(scene) as IFlowScene;
  } catch (e) {
    return scene as IFlowScene;
  }
}

export const PublishButtons = observer(() => {
  const {
    modsStore: { hasUnsavedChanges: hasUnsavedModsChanges },
    platformStore: {
      hasUnsavedConnectorKeyRenames,
      publishCurrentVersion,
      selectedProjectId,
      selectedProjectBasicInfo,
      updateVideoPartMods,
      publishedTime,
      projectWorkspaceVersion,
      savePerVideoSchema,
      saveLiveControlSchema,
      selectedProjectHasLiveVersion,
      selectedProjectIsLatestVersionPublished,
      projectWorkspaceFlowDiagram,
      updateSaveFlowDiagram,
      selectedVideoPart,
    },
    dynamicDataStore: {
      hasUnsavedChanges: hasUnsavedDynamicDataChanges,
      perVideoCurrentSchema,
      liveControlCurrentData,
      liveControlCurrentSchema,
      hasUnsavedChangesPerVideo,
      hasUnsavedChangesLiveControl,
    },
    layoutStore: { schemaDrawerOpen, toggleSchemaDrawer },
    playerStore: {
      jsonFile,
      player,
      hasUnsavedJsonDraftChanges,
      previewVideoPart,
    },
  } = useMst();
  const [publishButtonState, setPublishButtonState] = useState<AsyncOpState>(
    AsyncOpState.Untouched
  );
  const flowDiagramUnsavedChanges =
    projectWorkspaceFlowDiagram?.hasUnsavedChanges;
  const saveFlags = useMemo(
    () => ({
      hasUnsavedDynamicDataChanges,
      hasUnsavedModsChanges,
      hasUnsavedConnectorKeyRenames,
      hasUnsavedJsonDraftChanges,
      flowDiagramUnsavedChanges,
      hasUnsavedChangesPerVideo,
      hasUnsavedChangesLiveControl,
    }),
    [
      flowDiagramUnsavedChanges,
      hasUnsavedDynamicDataChanges,
      hasUnsavedChangesLiveControl,
      hasUnsavedChangesPerVideo,
      hasUnsavedJsonDraftChanges,
      hasUnsavedModsChanges,
      hasUnsavedConnectorKeyRenames,
    ]
  );
  const [modalUnsavedText, setModalUnsavedText] = useState("");
  const [modalSaveOpen, setModalSaveOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [scenes, setScenes] = useState<Array<IFlowScene>>(
    (selectedVideoPart && [selectedVideoPart.name]) || []
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [publishDropdownOpen, setPublishDropdownOpen] = useState(false);
  const publishButtonTexts = {
    [AsyncOpState.Untouched]: "Published",
    [AsyncOpState.Changed]: "Publish",
    [AsyncOpState.Saving]: "Publishing",
    [AsyncOpState.Success]: "Published",
    [AsyncOpState.Error]: "Error",
    Disabled: "Publish",
  };
  const [hasUnSavedDraftChanges, setHasUnSavedDraftChanges] = useState(false);
  const [demoWinOpen, setDemoWinOpen] = useState(false);
  const [flowMapOpen, setFlowMapOpen] = useState(false);
  const [flowMapLoaded, setFlowMapLoaded] = useState(false);
  const [flowMapResizing, setFlowMapResizing] = useState(false);
  const [flowMapSize, setFlowMapSize] = useState<FlowMapSize>(
    getDefaultFlowMapSize
  );
  const flowMapResizeCleanupRef = useRef<(() => void) | null>(null);
  const previewSceneValues = scenes.map(serializePreviewScene);

  const buildUnsavedText = useCallback(() => {
    return getUnsavedStudioText(saveFlags);
  }, [saveFlags]);

  const getPublishedVideoPartsNamesAndData = useCallback(async () => {
    if (!selectedProjectId) {
      return;
    }

    // Use customGetProject which already includes flowDiagram nodes/edges and excludes unauthorized fields
    const authToken = await getAccessTokenForLambdaAuth();
    const {
      data: { getProject: fetchedProject },
    } = (await client.graphql({
      query: customGetProject,
      variables: { id: selectedProjectId as string },
      authMode: "lambda",
      authToken,
    })) as { data: { getProject: Project } };

    setProject(fetchedProject);
  }, [selectedProjectId]);

  useEffect(() => {
    setHasUnSavedDraftChanges(hasUnsavedDraftChanges(saveFlags));
  }, [saveFlags]);

  useEffect(() => {
    if (selectedProjectId) {
      setScenes((selectedVideoPart && [selectedVideoPart.name]) || []);
      getPublishedVideoPartsNamesAndData();
    }
  }, [
    selectedProjectId,
    selectedProjectBasicInfo,
    selectedVideoPart,
    getPublishedVideoPartsNamesAndData,
  ]);

  useEffect(() => {
    if (isSaving) {
      setPublishButtonState(AsyncOpState.Saving);
    } else if (
      hasPublishableChanges(saveFlags, selectedProjectIsLatestVersionPublished)
    ) {
      setPublishButtonState(AsyncOpState.Changed);
    } else {
      setPublishButtonState(AsyncOpState.Untouched);
    }
  }, [selectedProjectIsLatestVersionPublished, isSaving, saveFlags]);
  // check has unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(
      hasPublishableChanges(saveFlags, selectedProjectIsLatestVersionPublished)
    );
  }, [
    selectedProjectIsLatestVersionPublished,
    selectedProjectBasicInfo?.updatedAt,
    projectWorkspaceVersion?.updatedAt,
    saveFlags,
  ]);
  function publishButtonClick() {
    const unsavedText = buildUnsavedText();
    if (unsavedText) {
      setModalUnsavedText(unsavedText);
    } else {
      setModalSaveOpen(true);
    }
  }
  async function publish() {
    setPublishButtonState(AsyncOpState.Saving);
    try {
      await publishCurrentVersion();
      setPublishButtonState(AsyncOpState.Untouched);
      setModalSaveOpen(false);
    } catch (e) {
      console.error("Publish failed:", e);
      setPublishButtonState(AsyncOpState.Untouched);
    }
  }
  async function saveChangesAndPublish() {
    setIsSaving(true);
    setPublishButtonState(AsyncOpState.Saving);
    try {
      await saveProject();
    } catch (e) {
      console.error("Save failed, skipping publish:", e);
      setIsSaving(false);
      setPublishButtonState(AsyncOpState.Untouched);
      return;
    }
    try {
      await publish();
    } finally {
      setModalUnsavedText("");
      setIsSaving(false);
    }
  }

  async function saveChanges() {
    setIsSavingDraft(true);
    try {
      await saveProject();
      setModalUnsavedText("");
    } catch (e) {
      console.error("Save failed:", e);
    } finally {
      setIsSavingDraft(false);
    }
  }
  async function saveProject() {
    try {
      await saveStudioDraftProject({
        flags: saveFlags,
        projectWorkspaceFlowDiagram,
        flowDiagram: flowsStore.flowDiagram,
        perVideoCurrentSchema,
        liveControlCurrentSchema,
        liveControlCurrentData,
        updateVideoPartMods,
        updateSaveFlowDiagram,
        savePerVideoSchema,
        saveLiveControlSchema,
      });
    } catch (e) {
      console.error("saveProject error:", e);
      throw e;
    }
  }

  useSaveProjectShortcut({
    canSave: hasUnSavedDraftChanges,
    isSaving: isSaving || isSavingDraft,
    isPaused: modalUnsavedText !== "" || modalSaveOpen || flowMapOpen,
    onSave: saveChanges,
  });

  function openLiveVersion(e?: MouseEvent<HTMLButtonElement>) {
    // Prevent event bubbling to avoid dropdown interference
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    if (!selectedProjectId) {
      return;
    }

    const publishedExampleData = project?.stateJsonSchemaStr
      ? JSON.parse(project.stateJsonSchemaStr).examples[0]
      : null;
    const version = PlayerManager.get().GetCurrentPlayerVersion();
    const url = getDemoPlayerUrl({
      projectId: selectedProjectId,
      env: getENV(),
      scenes,
      data: publishedExampleData,
      playerVersion: version,
    });

    // Close dropdown and open URL
    setPublishDropdownOpen(false);
    window.open(url, "_blank");
  }
  const lineups = project?.flowDiagram?.nodes?.reduce<Array<string>>(
    (acc, node) => {
      if (node.lineupId) {
        acc.push(node.lineupId);
      }
      return acc;
    },
    []
  );

  const tooltipSaveDraft = () => {
    let tooltipText: ReactNode = null;

    if (hasUnsavedDraftChanges(saveFlags)) {
      tooltipText = (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>You have unsaved changes:</span>
          <ul>
            {hasConnectorDraftChanges(saveFlags) && <li>Connectors</li>}
            {hasUnsavedJsonDraftChanges && <li>Animation</li>}
            {flowDiagramUnsavedChanges && <li>Flows</li>}
            {hasDataDraftChanges(saveFlags) && <li>Dynamic Data</li>}
          </ul>
        </div>
      );
    }
    return tooltipText;
  };

  // Get last edit text for the clock icon (shown outside)
  const lastEditText = projectWorkspaceVersion?.updatedAt
    ? savedDate2Relative(projectWorkspaceVersion.updatedAt)
    : null;

  // Get last published text for the dropdown
  const lastPublishedText =
    selectedProjectBasicInfo?.publishedAt || selectedProjectBasicInfo?.updatedAt
      ? savedDate2Relative(
          selectedProjectBasicInfo.publishedAt ||
            selectedProjectBasicInfo.updatedAt
        )
      : null;

  // Show last edit icon only when there are unpublished changes
  const showLastEditIcon =
    selectedProjectHasLiveVersion &&
    !selectedProjectIsLatestVersionPublished &&
    lastEditText;

  // Publish dropdown content
  const publishDropdownContent = (
    <div className="publish-dropdown-menu">
      {lastPublishedText && (
        <div className="last-edit-info">
          <span className="label">Last published</span>
          <span className="value">{lastPublishedText}</span>
        </div>
      )}
      {selectedProjectHasLiveVersion && (
        <>
          {lastPublishedText && <div className="dropdown-divider" />}
          <div className="scene-selector-section">
            <span className="section-label">Preview scenes</span>
            <Select
              id="view-live-select"
              className="scene-selector"
              placeholder="Select scenes to preview"
              value={previewSceneValues}
              tagRender={tagRender}
              onChange={(value) => {
                setScenes(value.map(parsePreviewScene));
              }}
              mode="multiple"
              size="small"
            >
              {lineups?.length ? (
                <>
                  <Select.OptGroup key="Flows">
                    {lineups.map((lineup) => (
                      <Select.Option
                        key={lineup}
                        value={JSON.stringify({ lineup })}
                      >
                        {lineup}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup key="Scenes">
                    {project?.videoParts?.map((scene) => (
                      <Select.Option key={scene.name} value={scene.name}>
                        {scene.name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                </>
              ) : (
                project?.videoParts?.map((scene) => (
                  <Select.Option key={scene.name} value={scene.name}>
                    {scene.name}
                  </Select.Option>
                ))
              )}
            </Select>
          </div>
          <Button
            type="default"
            className="preview-button"
            onClick={(e) =>
              openLiveVersion(e as unknown as MouseEvent<HTMLButtonElement>)
            }
          >
            Preview published version
          </Button>
        </>
      )}
    </div>
  );

  const handleFlowMapClick = () => {
    if (!selectedProjectId) {
      return;
    }

    setFlowMapLoaded(false);
    setFlowMapSize(getDefaultFlowMapSize());
    setFlowMapOpen(true);
  };

  const stopFlowMapResize = useCallback(() => {
    flowMapResizeCleanupRef.current?.();
    flowMapResizeCleanupRef.current = null;
    setFlowMapResizing(false);
  }, []);

  useEffect(() => {
    return () => {
      flowMapResizeCleanupRef.current?.();
      flowMapResizeCleanupRef.current = null;
    };
  }, []);

  const handleFlowMapResizeStart = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      flowMapResizeCleanupRef.current?.();
      setFlowMapResizing(true);

      const startX = event.clientX;
      const startY = event.clientY;
      const startSize = flowMapSize;
      const maxWidth = Math.max(
        360,
        window.innerWidth - FLOW_MAP_VIEWPORT_PADDING
      );
      const maxHeight = Math.max(
        420,
        window.innerHeight - FLOW_MAP_VIEWPORT_PADDING
      );
      const minWidth = Math.min(FLOW_MAP_MIN_WIDTH, maxWidth);
      const minHeight = Math.min(FLOW_MAP_MIN_HEIGHT, maxHeight);

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        setFlowMapSize({
          width: clamp(
            startSize.width + moveEvent.clientX - startX,
            minWidth,
            maxWidth
          ),
          height: clamp(
            startSize.height + moveEvent.clientY - startY,
            minHeight,
            maxHeight
          ),
        });
      };

      const cleanupResizeListeners = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      const handleMouseUp = () => {
        cleanupResizeListeners();
        flowMapResizeCleanupRef.current = null;
        setFlowMapResizing(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      flowMapResizeCleanupRef.current = cleanupResizeListeners;
    },
    [flowMapSize]
  );

  return (
    <>
      <div className="PublishButtons">
        <HoverTooltip
          title="Flow Map"
          description="Open the project flow diagram to review scene paths and lineup transitions."
          placement="bottom"
        >
          <span className="top-bar-tooltip-target">
            <Button
              type="text"
              shape="round"
              className="flow-map-button"
              icon={<FlowMapIcon />}
              onClick={handleFlowMapClick}
              disabled={!selectedProjectId}
            >
              Flow Map
            </Button>
          </span>
        </HoverTooltip>
        <HoverTooltip
          title="Data"
          description="Edit the dynamic data and live control values used by this project."
          placement="bottom"
        >
          <span className="top-bar-tooltip-target">
            <Button
              type="text"
              shape="round"
              className={classNames("data-button", {
                active: schemaDrawerOpen,
              })}
              icon={<DataIcon />}
              onClick={toggleSchemaDrawer}
            >
              Data
            </Button>
          </span>
        </HoverTooltip>
        {showLastEditIcon && (
          <Tooltip
            styles={{ root: { width: "100%" } }}
            title={`Last edit was ${lastEditText}`}
            placement="left"
          >
            <Button
              type="text"
              shape="circle"
              className="last-edit-button"
              icon={<TimeIcon />}
            />
          </Tooltip>
        )}
        <Tooltip
          placement="bottom"
          title={tooltipSaveDraft}
          trigger={tooltipSaveDraft() ? "hover" : "click"}
        >
          <Button
            className={classNames("save-draft-button", {
              saving: isSaving || isSavingDraft,
            })}
            onClick={saveChanges}
            disabled={!hasUnSavedDraftChanges || isSavingDraft || isSaving}
          >
            {isSaving || isSavingDraft ? (
              <>
                <span>Saving...</span> <LoadingOutlined />
              </>
            ) : (
              <span>Save Project</span>
            )}
          </Button>
        </Tooltip>
        {projectWorkspaceVersion && (
          <HoverTooltip
            title="View Draft"
            description="Draft is your editable version. Save keeps changes in the draft; Publish makes them live."
            placement="bottom"
          >
            <span className="top-bar-tooltip-target">
              <Button
                className="view-draft-button"
                onClick={() => {
                  // Pause the player before opening demo window
                  if (player?.animation && !player.animation.isPaused) {
                    player.pause();
                    (player as any).pausedByUser = true;
                  }
                  setDemoWinOpen(true);
                }}
              >
                View Draft
              </Button>
            </span>
          </HoverTooltip>
        )}
        {demoWinOpen && projectWorkspaceVersion && (
          <DemoWindow
            draftScene={previewVideoPart}
            openWin={setDemoWinOpen}
            selectedVideoPart={selectedVideoPart}
            project={{
              id: projectWorkspaceVersion.id,
              env: getENV(),
              version: "latest",
            }}
            posterFrame={player?.posterFrame || 0}
            autoplay={false}
            height={jsonFile?.h || 720}
            liveControlData={liveControlCurrentData}
            videoPartNames={
              projectWorkspaceVersion.videoParts?.map((vp) => vp.name) || []
            }
            data={
              projectWorkspaceVersion.stateJsonSchemaStr
                ? JSON.parse(projectWorkspaceVersion.stateJsonSchemaStr)
                    .examples[0]
                : {}
            }
          />
        )}
        <div className="publish-button-wrapper">
          <Tooltip
            placement="left"
            title={publishedTime}
            styles={{ root: { maxWidth: "500px" } }}
          >
            <BlingsBtn
              disabled={!hasUnsavedChanges || isSaving || isSavingDraft}
              opState={publishButtonState}
              htmlType={"submit"}
              btnTexts={publishButtonTexts}
              onClick={() => {
                publishButtonClick();
              }}
              className={classNames({
                secondary: isSavingDraft,
              })}
            />
          </Tooltip>
          {selectedProjectHasLiveVersion && (
            <Dropdown
              open={publishDropdownOpen}
              onOpenChange={setPublishDropdownOpen}
              popupRender={() => publishDropdownContent}
              placement="bottomRight"
              trigger={["click"]}
              overlayClassName="publish-dropdown-overlay"
            >
              <button className="publish-chevron-button" type="button">
                <DownOutlined className="chevron-icon" />
              </button>
            </Dropdown>
          )}
        </div>
      </div>
      <Modal
        className="flow-map-modal"
        open={flowMapOpen}
        onCancel={() => {
          setFlowMapOpen(false);
          setFlowMapLoaded(false);
          stopFlowMapResize();
        }}
        footer={null}
        title="Flow Map"
        width={flowMapSize.width}
        style={
          {
            "--flow-map-modal-height": `${flowMapSize.height}px`,
            "--flow-map-header-height": `${FLOW_MAP_HEADER_HEIGHT}px`,
          } as CSSProperties
        }
        destroyOnClose
      >
        {selectedProjectId && (
          <div
            className={classNames("flow-map-frame-shell", {
              resizing: flowMapResizing,
            })}
          >
            {!flowMapLoaded && (
              <div className="flow-map-loader">
                <Spin />
                <span className="flow-map-loader-text">Loading Flow Map</span>
              </div>
            )}
            <iframe
              className="flow-map-iframe"
              src={getFlowMapEmbedUrl(selectedProjectId)}
              title="Flow Map"
              onLoad={() => setFlowMapLoaded(true)}
            />
          </div>
        )}
        <button
          type="button"
          className="flow-map-resize-handle"
          aria-label="Resize Flow Map"
          onMouseDown={handleFlowMapResizeStart}
        />
      </Modal>
      <Modal
        className="unsaved-changes-modal"
        zIndex={SAVE_MODAL_Z_INDEX}
        open={modalUnsavedText !== ""}
        onCancel={() => {
          setModalUnsavedText("");
        }}
        footer={
          <div className="unsaved-changes-modal__footer">
            <button
              key="ok"
              type="button"
              className="unsaved-changes-modal__text-button"
              onClick={() => {
                setModalUnsavedText("");
              }}
            >
              Close
            </button>
            <BlingsBtn
              key="publish"
              className="publish unsaved-changes-modal__primary-button"
              opState={publishButtonState}
              htmlType={"submit"}
              btnTexts={{
                [AsyncOpState.Changed]: "Save and Publish",
              }}
              onClick={saveChangesAndPublish}
            />
          </div>
        }
      >
        <div className="popup-text">{modalUnsavedText}</div>
      </Modal>
      <Modal
        className="unsaved-changes-modal"
        zIndex={SAVE_MODAL_Z_INDEX}
        open={modalSaveOpen}
        onCancel={() => {
          setModalSaveOpen(false);
        }}
        footer={
          <div className="unsaved-changes-modal__footer">
            <button
              key="cancel"
              type="button"
              className="unsaved-changes-modal__text-button"
              onClick={() => {
                setModalSaveOpen(false);
              }}
            >
              Cancel
            </button>
            <BlingsBtn
              key="publish"
              className="publish unsaved-changes-modal__primary-button"
              opState={publishButtonState}
              htmlType={"submit"}
              btnTexts={publishButtonTexts}
              onClick={publish}
            />
          </div>
        }
      >
        <div className="popup-text">
          Are you sure you want to publish the current version?
        </div>
      </Modal>
    </>
  );
});
const tagRender = (props) => {
  const { label, value, closable, onClose } = props;
  const isLineup =
    value &&
    (() => {
      try {
        return JSON.parse(value).lineup != null;
      } catch (e) {}
      return false;
    })();

  return (
    <Tag
      closable={closable}
      onClose={onClose}
      style={{
        backgroundColor: isLineup ? "rgba(255, 21, 77, 0.15)" : "#3f3f46",
        borderColor: isLineup ? "rgba(255, 21, 77, 0.3)" : "#52525b",
        color: isLineup ? "#ff7a9a" : "#e4e4e7",
        borderRadius: 4,
        fontSize: 12,
        margin: 0,
        marginInlineEnd: 4,
      }}
    >
      {label}
    </Tag>
  );
};
